/**
 * data.js — Runtime brand data loader
 *
 * Brand data is NOT bundled into main.js. Instead it is fetched at runtime
 * from /brands.dat (an XOR-encoded binary file generated at build time by
 * scripts/encode-brands.mjs). This prevents the full marker database from
 * appearing as readable plain text in the JS bundle.
 *
 * Consumers (palette.js, paletteCode.js, pages) import the mutable references
 * below. By the time any function that reads the data is called, loadBrands()
 * will have already resolved and these arrays/objects will be populated.
 */

const XOR_KEY = 0x5A;

// Mutable exports — start empty, populated by loadBrands()
// Consumers import these as live bindings; mutations here are visible to them.
export let markers = [];
export let BRANDS = [];
export let OHUHU_SET_LEGEND = {};
export let ARRTX_SET_LEGEND = {};
export let SFAIH_SET_LEGEND = {};
export let CALIART_SETS = {};
export let BIANYO_SETS = {};
export let HOTU_SETS = {};
export let CALM_OVER_CHAOS_SETS = {};
export let HANGFAN_SETS = {};
export let SANJOKI_SET_LEGEND = {};
export let PRISM_SETS = {};
export let ROYAL_LANGNICKEL_SETS = {};
export let COPIC_SKETCH_SETS = {};
export let COPIC_CLASSIC_SETS = {};
export let COPIC_CIAO_SETS = {};

// Individual brand arrays — populated after load (backward compat)
export let ohuhuMarkers = [];
export let arrtxMarkers = [];
export let decotimeMarkers = [];
export let tongfushopMarkers = [];
export let touchMarkers = [];
export let touchNewMarkers = [];
export let shuttleartMarkers = [];
export let deliMarkers = [];
export let chenruiMarkers = [];
export let noteMarkers = [];
export let pingodoceMarkers = [];
export let lidlMarkers = [];
export let promarkerMarkers = [];
export let cchobbyMarkers = [];
export let sfaihMarkers = [];
export let caliartMarkers = [];
export let bianyoMarkers = [];
export let hotuMarkers = [];
export let calmOverChaosMarkers = [];
export let hangfanMarkers = [];
export let sanjokiMarkers = [];
export let prismMarkers = [];
export let royalLangnickelMarkers = [];
export let copicSketchMarkers = [];
export let copicClassicMarkers = [];
export let copicCiaoMarkers = [];

let _loadPromise = null;

export function loadBrands() {
  if (_loadPromise) return _loadPromise;

  _loadPromise = fetch('/brands.dat')
    .then(r => {
      if (!r.ok) throw new Error(`Failed to load brands.dat: ${r.status}`);
      return r.arrayBuffer();
    })
    .then(buf => {
      const bytes = new Uint8Array(buf);
      // XOR decode
      const decoded = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) {
        decoded[i] = bytes[i] ^ XOR_KEY;
      }
      const json = new TextDecoder().decode(decoded);
      const data = JSON.parse(json);

      // Populate mutable exports in-place so existing imports see the data
      markers.push(...data.markers);
      BRANDS.push(...data.BRANDS);
      Object.assign(OHUHU_SET_LEGEND, data.OHUHU_SET_LEGEND);
      Object.assign(ARRTX_SET_LEGEND, data.ARRTX_SET_LEGEND);
      Object.assign(SFAIH_SET_LEGEND, data.SFAIH_SET_LEGEND);
      Object.assign(CALIART_SETS, data.CALIART_SETS);
      Object.assign(BIANYO_SETS, data.BIANYO_SETS);
      Object.assign(HOTU_SETS, data.HOTU_SETS);
      Object.assign(CALM_OVER_CHAOS_SETS, data.CALM_OVER_CHAOS_SETS);
      Object.assign(HANGFAN_SETS, data.HANGFAN_SETS);
      Object.assign(SANJOKI_SET_LEGEND, data.SANJOKI_SET_LEGEND);
      Object.assign(PRISM_SETS, data.PRISM_SETS);
      Object.assign(ROYAL_LANGNICKEL_SETS, data.ROYAL_LANGNICKEL_SETS);
      Object.assign(COPIC_SKETCH_SETS, data.COPIC_SKETCH_SETS);
      Object.assign(COPIC_CLASSIC_SETS, data.COPIC_CLASSIC_SETS);
      Object.assign(COPIC_CIAO_SETS, data.COPIC_CIAO_SETS);

      // Populate individual brand arrays
      const brandArrayMap = {
        'Ohuhu': ohuhuMarkers,
        'Arrtx': arrtxMarkers,
        'Decotime': decotimeMarkers,
        'Tongfushop': tongfushopMarkers,
        'Touch': touchMarkers,
        'TouchNew': touchNewMarkers,
        'Shuttle Art': shuttleartMarkers,
        'Deli': deliMarkers,
        'Chen Rui': chenruiMarkers,
        'NOTE': noteMarkers,
        'Pingo Doce': pingodoceMarkers,
        'Lidl': lidlMarkers,
        'Promarker': promarkerMarkers,
        'CCHobby': cchobbyMarkers,
        'SFAIH': sfaihMarkers,
        'Caliart': caliartMarkers,
        'Bianyo': bianyoMarkers,
        'Hotu': hotuMarkers,
        'Calm Over Chaos': calmOverChaosMarkers,
        'Hangfan': hangfanMarkers,
        'Sanjoki': sanjokiMarkers,
        'Prism': prismMarkers,
        'Royal & Langnickel': royalLangnickelMarkers,
        'Copic - Sketch': copicSketchMarkers,
        'Copic - Classic': copicClassicMarkers,
        'Copic - Ciao': copicCiaoMarkers,
      };
      for (const m of data.markers) {
        const arr = brandArrayMap[m.brand];
        if (arr) arr.push(m);
      }
    });

  return _loadPromise;
}
